export const MIN_VIEWPORT_MARGIN = 8;
export const PANEL_MIN_FLOOR_WIDTH = 275;
export const PANEL_MIN_FLOOR_HEIGHT = 300;

export type ChecklistLayoutInput = {
  position: string;
  dimensions: { width: number; height: number };
  offsets: { vertical: number; horizontal: number };
  viewport: { width: number; height: number };
  applyMinFloor?: boolean;
  referenceDimensions?: { width: number; height: number };
};

export type ChecklistLayoutOutput = {
  dimensions: { width: number; height: number };
  offsets: { vertical: number; horizontal: number };
};

const isHorizontalCenter = (position: string): boolean =>
  position === 'top-center' || position === 'bottom-center';

/** Aba vertical colada na borda, centralizada na altura: `vertical` desloca a partir do centro. */
export const isEdgePosition = (position?: string | null): boolean =>
  position === 'edge-left' || position === 'edge-right';

export const clampChecklistLayout = (input: ChecklistLayoutInput): ChecklistLayoutOutput => {
  const {
    position,
    dimensions,
    offsets,
    viewport,
    applyMinFloor = true,
    referenceDimensions
  } = input;

  const safeViewportW = Math.max(0, viewport.width);
  const safeViewportH = Math.max(0, viewport.height);

  const widthBudget = Math.max(0, safeViewportW - 2 * MIN_VIEWPORT_MARGIN);
  const heightBudget = Math.max(0, safeViewportH - 2 * MIN_VIEWPORT_MARGIN);

  const widthFloor = applyMinFloor ? Math.min(PANEL_MIN_FLOOR_WIDTH, widthBudget) : 0;
  const heightFloor = applyMinFloor ? Math.min(PANEL_MIN_FLOOR_HEIGHT, heightBudget) : 0;

  const clampedWidth = Math.max(widthFloor, Math.min(dimensions.width, widthBudget));
  const clampedHeight = Math.max(heightFloor, Math.min(dimensions.height, heightBudget));

  const referenceWidthRaw = referenceDimensions?.width ?? dimensions.width;
  const referenceHeightRaw = referenceDimensions?.height ?? dimensions.height;
  const referenceWidth = Math.max(clampedWidth, Math.min(referenceWidthRaw, widthBudget));
  const referenceHeight = Math.max(clampedHeight, Math.min(referenceHeightRaw, heightBudget));

  if (isEdgePosition(position)) {
    const maxShift = Math.max(0, (safeViewportH - referenceHeight) / 2 - MIN_VIEWPORT_MARGIN);
    const vertical = Number.isFinite(offsets.vertical) ? offsets.vertical : 0;
    return {
      dimensions: { width: clampedWidth, height: clampedHeight },
      offsets: { vertical: Math.max(-maxShift, Math.min(vertical, maxShift)), horizontal: 0 }
    };
  }

  let clampedHorizontal = offsets.horizontal;
  if (!isHorizontalCenter(position)) {
    const maxHorizontalOffset = Math.max(
      MIN_VIEWPORT_MARGIN,
      safeViewportW - referenceWidth - MIN_VIEWPORT_MARGIN
    );
    clampedHorizontal = Math.max(0, Math.min(offsets.horizontal, maxHorizontalOffset));
  }

  const maxVerticalOffset = Math.max(
    MIN_VIEWPORT_MARGIN,
    safeViewportH - referenceHeight - MIN_VIEWPORT_MARGIN
  );
  const clampedVertical = Math.max(0, Math.min(offsets.vertical, maxVerticalOffset));

  return {
    dimensions: { width: clampedWidth, height: clampedHeight },
    offsets: { vertical: clampedVertical, horizontal: clampedHorizontal }
  };
};

export type LanguageFlagsCorner = 'top' | 'bottom';

/**
 * Em que canto direito a pilha de bandeiras de idioma aparece no editor.
 *
 * Padrão é o superior. Quando o próprio checklist ocupa esse canto, as bandeiras descem para o
 * inferior — senão ficariam por cima do painel que o usuário está tentando ver traduzido.
 *
 * Só `top-right` colide: `center` e ausente são normalizados para `bottom-right` pelo próprio
 * componente do checklist, e as posições `-left` e `-center` não disputam o lado direito.
 */
export const resolveLanguageFlagsCorner = (launcherPosition?: string): LanguageFlagsCorner =>
  launcherPosition === 'top-right' ? 'bottom' : 'top';
